/**
 * PermisoEntity - Entidad de Dominio
 *
 * Representa los permisos del usuario autenticado en una empresa.
 * Permisos con formato: MODULO_ACCION (ej: CLIENTE_VER, GREMIO_CREAR)
 */

export type PermisoAccion = 'VER' | 'CREAR' | 'EDITAR' | 'ELIMINAR';

export type PermisoModulo =
  | 'GREMIO' | 'REPARTICION' | 'CONCEPTO' | 'SUCURSAL' | 'INTERES' | 'AMORTIZACION'
  | 'CLIENTE' | 'CUENTA' | 'CREDITO' | 'SCORING'
  | 'CAJA' | 'APORTE'
  | 'EMPRESA' | 'USUARIO'
  | 'CONFIGURACION' | 'PERFIL'
  | 'DASHBOARD';

export type PermisoKey = `${PermisoModulo}_${PermisoAccion}`;

/**
 * Representa un permiso individual del catálogo del sistema
 * Response de: GET /api/permiso/v1/listado
 */
export interface PermisoItemData {
  id: number;
  nombre: string;
  modulo: string;
  accion: string;
}

export class PermisoItemEntity {
  private constructor(
    public readonly id: number,
    public readonly nombre: string,
    public readonly modulo: string,
    public readonly accion: string
  ) {
    Object.freeze(this);
  }

  static fromPersistence(data: PermisoItemData): PermisoItemEntity {
    return new PermisoItemEntity(data.id, data.nombre, data.modulo, data.accion);
  }
}

export interface MisPermisosData {
  usuarioId: number;
  nombreUsuario: string;
  empresaId: number;
  nombreEmpresa: string;
  permisos: string[];
  permisosPorModulo: Record<string, string[]>;
}

export class PermisoEntity {
  private constructor(
    public readonly usuarioId: number,
    public readonly nombreUsuario: string,
    public readonly empresaId: number,
    public readonly nombreEmpresa: string,
    public readonly permisos: string[],
    public readonly permisosPorModulo: Record<string, string[]>
  ) {
    Object.freeze(this);
  }

  static fromPersistence(data: MisPermisosData): PermisoEntity {
    return new PermisoEntity(
      data.usuarioId,
      data.nombreUsuario,
      data.empresaId,
      data.nombreEmpresa,
      data.permisos ?? [],
      data.permisosPorModulo ?? {}
    );
  }

  tienePermiso(permiso: string): boolean {
    return this.permisos.includes(permiso);
  }

  tieneAlgunPermiso(permisos: string[]): boolean {
    return permisos.some(p => this.permisos.includes(p));
  }

  tieneTodosLosPermisos(permisos: string[]): boolean {
    return permisos.every(p => this.permisos.includes(p));
  }

  puedeVer(modulo: string): boolean {
    return this.tienePermiso(`${modulo}_VER`);
  }

  puedeCrear(modulo: string): boolean {
    return this.tienePermiso(`${modulo}_CREAR`);
  }

  puedeEditar(modulo: string): boolean {
    return this.tienePermiso(`${modulo}_EDITAR`);
  }

  puedeEliminar(modulo: string): boolean {
    return this.tienePermiso(`${modulo}_ELIMINAR`);
  }

  getAccionesModulo(modulo: string): string[] {
    return this.permisosPorModulo[modulo] ?? [];
  }
}
